import { Link } from "react-router-dom";
import { MapPin, ArrowRight } from "lucide-react";

const LOCATIONS = [
  {
    city: "Sydney",
    state: "NSW",
    blurb: "Our largest market. Same-day vehicle inspections across Greater Sydney.",
    image:
      "https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?auto=format&fit=crop&w=1200&q=80",
  },
  {
    city: "Melbourne",
    state: "VIC",
    blurb: "Servicing inner city, suburbs and regional Victoria.",
    image:
      "https://images.unsplash.com/photo-1545044846-351ba102b6d5?auto=format&fit=crop&w=1200&q=80",
  },
  {
    city: "Brisbane",
    state: "QLD",
    blurb: "Tradie utes and SUVs ready across South-East Queensland.",
    image:
      "https://images.unsplash.com/photo-1597983073512-cf0c5b75d33d?auto=format&fit=crop&w=1200&q=80",
  },
  {
    city: "Perth",
    state: "WA",
    blurb: "Full fleet access across Perth and the Western Suburbs.",
    image:
      "https://images.unsplash.com/photo-1573935448851-c12ada4c8aae?auto=format&fit=crop&w=1200&q=80",
  },
  {
    city: "Adelaide",
    state: "SA",
    blurb: "Servicing metro Adelaide and surrounding business districts.",
    image:
      "https://images.unsplash.com/photo-1576487236230-eaa4afe68192?auto=format&fit=crop&w=1200&q=80",
  },
  {
    city: "Canberra",
    state: "ACT",
    blurb: "Specialist support for ACT business owners and contractors.",
    image:
      "https://images.unsplash.com/photo-1602673221577-0b56d7ce446b?auto=format&fit=crop&w=1200&q=80",
  },
  {
    city: "Hobart",
    state: "TAS",
    blurb: "Reliable rent-to-own access across Tasmania.",
    image:
      "https://images.unsplash.com/photo-1572783649660-44a40d8717e9?auto=format&fit=crop&w=1200&q=80",
  },
  {
    city: "Darwin",
    state: "NT",
    blurb: "4x4s, utes and SUVs built for Top End conditions.",
    image:
      "https://images.unsplash.com/photo-1602002418082-a4443e081dd1?auto=format&fit=crop&w=1200&q=80",
  },
];

export default function Locations() {
  return (
    <div data-testid="locations-page" className="bg-[#050505]">
      {/* Hero */}
      <section className="relative py-28 md:py-40 overflow-hidden bg-grain">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?auto=format&fit=crop&w=2000&q=80')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/70 to-[#050505]" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-12">
          <div className="label-eyebrow mb-6 fade-up">Locations</div>
          <h1
            className="font-display text-white leading-[1.05] fade-up fade-up-delay-1"
            style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)", fontWeight: 300 }}
          >
            One programme. <br />
            <span className="italic text-[#D4AF37]">Every capital city.</span>
          </h1>
          <p className="text-white/70 max-w-2xl mt-8 text-lg fade-up fade-up-delay-2">
            From Sydney's CBD to Darwin's Top End, our rent-to-own service is available across
            Australia. Pick your city and we'll handle the rest.
          </p>
        </div>
      </section>

      {/* Locations grid */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {LOCATIONS.map((l, i) => (
              <article
                key={l.city}
                data-testid={`location-${l.city.toLowerCase()}`}
                className="group relative overflow-hidden border border-white/10 hover:border-[#D4AF37]/40 transition-all bg-[#0a0a0a]"
              >
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={l.image}
                    alt={l.city}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                </div>
                <div className="absolute inset-0 p-6 flex flex-col justify-between">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-[0.25em] text-white/70 bg-black/40 backdrop-blur px-2 py-1 border border-white/10">
                      0{i + 1}
                    </span>
                    <MapPin size={16} className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.22em] text-[#D4AF37] mb-2">
                      {l.state}
                    </div>
                    <h3 className="font-display text-3xl text-white mb-2" style={{ fontWeight: 400 }}>
                      {l.city}
                    </h3>
                    <p className="text-xs text-white/70 leading-relaxed">{l.blurb}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage strip */}
      <section className="py-24 md:py-32 bg-[#080808] border-y border-white/5">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <div className="label-eyebrow mb-6">Nationwide</div>
          <h2 className="font-display text-white text-4xl md:text-5xl leading-tight" style={{ fontWeight: 300 }}>
            Not in a capital? <br />
            <span className="italic text-[#D4AF37]">We still deliver.</span>
          </h2>
          <p className="text-white/60 mt-6 max-w-2xl mx-auto">
            We service regional and remote areas across Australia. Talk to our team about delivery
            and inspection in your area.
          </p>
          <Link
            to="/contact"
            data-testid="locations-contact-cta"
            className="inline-flex mt-10 items-center gap-2 border border-white/20 text-white px-8 py-4 text-sm tracking-wide hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
          >
            Talk to a specialist <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
